import { useState, useMemo } from 'react';
import { useDeepCompareEffect } from 'ahooks';
import { fetchMetricsDateRanges } from '@intelligence/v3/services/topCharts';
import { isEmpty } from '@intelligence/v3/utils/common';

const generateDateRangeMapKey = (groupBy?: string[] | null, dateRange?: IMetricDateRange): string => {
  if (!dateRange) return '';
  if (!dateRange?.metric) return '';

  const { granularity, market, metric } = dateRange || {};
  let key = `${granularity}`;

  if (groupBy?.includes('market')) {
    key += `__${market}`;
  }

  return `${key}__${metric}`;
};

export default function useMetricsDateRange(executable: boolean, props: Partial<IMetricsDateRangesFetchParams>) {
  const { game_ids: gameIds, app_ids: appIds, granularities, markets, metrics, group_by: groupBy } = props;

  const [loading, setLoading] = useState(true);
  const [dateRanges, setDateRanges] = useState<IMetricDateRange[]>([]);

  const dateRangeMap = useMemo<IRecord<IMetricDateRange>>(() => {
    if (loading) {
      return {};
    }

    if (isEmpty(dateRanges)) {
      return {};
    }

    return dateRanges!.reduce<IRecord<IMetricDateRange>>((acc, item) => {
      const key = generateDateRangeMapKey(groupBy, item);

      if (!!key) {
        if (!!item?.min_date && !!item?.max_date) {
          acc[key] = item;
        }
      }

      return acc;
    }, {});
  }, [loading, dateRanges]);

  const granularityDateRangeMap = useMemo<Partial<Record<IGranularity, IMetricDateRange>>>(() => {
    if (loading) {
      return {};
    }

    if (isEmpty(dateRanges)) {
      return {};
    }

    const getNewDate = (flag: 'min_date' | 'max_date', currentDate?: string, comingDate?: string): string => {
      if (isEmpty(currentDate)) {
        return comingDate || '';
      }

      if (isEmpty(comingDate)) {
        return currentDate!;
      }

      if (flag === 'min_date') {
        return (comingDate as string) < (currentDate as string) ? comingDate! : currentDate!;
      }

      if (flag === 'max_date') {
        return (comingDate as string) > (currentDate as string) ? comingDate! : currentDate!;
      }

      return '';
    };

    // 按粒度统计日期范围
    return dateRanges.reduce<Partial<Record<IGranularity, IMetricDateRange>>>((acc, item) => {
      const { granularity, min_date: minDate, max_date: maxDate } = item || {};

      if (!acc[granularity]) {
        // 如果该粒度还没有记录,直接使用当前项
        acc[granularity] = { ...item };
      } else {
        // 如果已有记录,更新 min_date 和 max_date
        const existing = acc[granularity]!;
        acc[granularity] = {
          ...existing,
          min_date: getNewDate('min_date', existing.min_date, minDate),
          max_date: getNewDate('max_date', existing.max_date, maxDate),
        };
      }

      return acc;
    }, {});
  }, [loading, dateRanges]);

  const updateDateRanges = async (params: IMetricsDateRangesFetchParams) => {
    try {
      setLoading(true);
      const res = await fetchMetricsDateRanges(params);
      setDateRanges(res?.data || []);
    } catch (error) {
      console.error(error);
      setDateRanges([]);
    } finally {
      setLoading(false);
    }
  };

  useDeepCompareEffect(() => {
    if (executable) {
      updateDateRanges({
        game_ids: gameIds || [],
        app_ids: appIds || [],
        granularities: granularities || [],
        markets: markets || [],
        metrics: metrics || [],
        group_by: groupBy || [],
      });
    }
  }, [executable, gameIds, appIds, granularities, markets, metrics, groupBy]);

  return { loading, dateRanges, dateRangeMap, granularityDateRangeMap };
}
